import { PromptCard } from "@/components/ai/prompt-card";
import { PromptDialog } from "@/components/ai/prompt-dialog";
import { UnifiedListLayout } from "@/components/layout/unified-list-layout";
import { UnifiedSearch } from "@/components/shared/UnifiedSearch";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useApp } from "@/contexts/app-context";
import {
    AiPrompt,
    deleteAiPrompt,
    getPromptsForContext,
    PromptType,
    updateAiPrompt
} from "@/services/supabase/ai-prompts";
import { Loader2, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PromptsIntroCard } from "./components/prompts-intro-card";

export default function AiPromptsPage() {
    const [prompts, setPrompts] = useState<AiPrompt[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState<AiPrompt | null>(null);
    const [initialDialogType, setInitialDialogType] = useState<PromptType | undefined>(undefined);
    const [searchQuery, setSearchQuery] = useState("");
    const { toast } = useToast();
    const { projectId } = useParams();
    const { activeStoryId } = useApp();

    const loadPrompts = async () => {
        if (!projectId) return;

        try {
            setIsLoading(true);
            // Fetch prompts for Project AND Story (excluding global)
            const data = await getPromptsForContext(projectId, activeStoryId);
            setPrompts(data);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load prompts",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPrompts();
    }, []);

    const filteredPrompts = useMemo(() => {
        if (!searchQuery) return prompts;
        const lowerQuery = searchQuery.toLowerCase();
        return prompts.filter(p =>
            p.name.toLowerCase().includes(lowerQuery) ||
            p.content.toLowerCase().includes(lowerQuery)
        );
    }, [prompts, searchQuery]);

    const handleDelete = async (id: number) => {
        try {
            await deleteAiPrompt(id);
            setPrompts(prompts.filter((p) => p.id !== id));
            toast({
                title: "Success",
                description: "Prompt deleted successfully",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete prompt",
                variant: "destructive",
            });
        }
    };

    const handleToggleActive = async (prompt: AiPrompt, checked: boolean) => {
        try {
            // Optimistic update
            setPrompts(prompts.map(p => p.id === prompt.id ? { ...p, is_active: checked } : p));

            await updateAiPrompt({
                id: prompt.id,
                is_active: checked
            });
        } catch (error) {
            // Revert on error
            setPrompts(prompts.map(p => p.id === prompt.id ? { ...p, is_active: !checked } : p));
            toast({
                title: "Error",
                description: "Failed to update status",
                variant: "destructive",
            });
        }
    };

    const writingActions = useMemo(() =>
        filteredPrompts.filter(p => p.type === PromptType.WritingAction),
        [filteredPrompts]);

    const systemInstructions = useMemo(() =>
        filteredPrompts.filter(p => p.type === PromptType.SystemInstruction),
        [filteredPrompts]);

    return (
        <UnifiedListLayout
            toolbar={
                <div className="flex items-center gap-4 w-full">
                    <UnifiedSearch
                        placeholder="Search prompts..."
                        value={searchQuery}
                        onChange={setSearchQuery}
                        className="w-full max-w-sm"
                    />
                    <Button
                        onClick={() => {
                            setEditingPrompt(null);
                            setInitialDialogType(undefined);
                            setIsDialogOpen(true);
                        }}
                        className="h-10 px-4 shadow-lg shadow-primary/20 ml-auto"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        New Prompt
                    </Button>
                </div>
            }
        >
            <div className="space-y-8">
                <PromptsIntroCard
                    onCreateWritingAction={() => {
                        setEditingPrompt(null);
                        setInitialDialogType(PromptType.WritingAction);
                        setIsDialogOpen(true);
                    }}
                    onCreateSystemInstruction={() => {
                        setEditingPrompt(null);
                        setInitialDialogType(PromptType.SystemInstruction);
                        setIsDialogOpen(true);
                    }}
                />


                {isLoading ? (
                    <div className="flex justify-center p-8">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                ) : filteredPrompts.length === 0 ? (
                    <div className="text-center p-16 border border-dashed border-border/60 rounded-xl text-muted-foreground bg-muted/5">
                        {searchQuery ? "No prompts match your search." : "No prompts found. Create one to get started."}
                    </div>
                ) : (
                    <div className="space-y-10">
                        {/* Writing Actions Section */}
                        {writingActions.length > 0 && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 px-1">
                                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Writing Actions</h3>
                                    <div className="h-px bg-border/40 flex-1" />
                                </div>
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
                                    {writingActions.map(prompt => (
                                        <PromptCard
                                            key={prompt.id}
                                            prompt={prompt}
                                            onEdit={() => { setEditingPrompt(prompt); setIsDialogOpen(true); }}
                                            onDelete={() => handleDelete(prompt.id)}
                                            onToggleActive={(checked) => handleToggleActive(prompt, checked)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* System Instructions Section */}
                        {systemInstructions.length > 0 && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 px-1">
                                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">System Instructions</h3>
                                    <div className="h-px bg-border/40 flex-1" />
                                </div>
                                <div className="grid gap-3">
                                    {systemInstructions.map(prompt => (
                                        <PromptCard
                                            key={prompt.id}
                                            prompt={prompt}
                                            onEdit={() => { setEditingPrompt(prompt); setIsDialogOpen(true); }}
                                            onDelete={() => handleDelete(prompt.id)}
                                            onToggleActive={(checked) => handleToggleActive(prompt, checked)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <PromptDialog
                    open={isDialogOpen}
                    onOpenChange={setIsDialogOpen}
                    projectId={projectId}
                    prompt={editingPrompt}
                    initialType={initialDialogType}
                    onSuccess={() => {
                        setIsDialogOpen(false);
                        loadPrompts();
                    }}
                    onDelete={handleDelete}
                />
            </div>
        </UnifiedListLayout>
    );
}
